import React, { useState } from 'react';
import Image from './AppImage';
import Icon from './AppIcon';
import Button from './ui/Button';
import { whatsappService } from '../services/whatsappService';
import useCartStore from '../store/cartStore';
import { cacheUtils } from '../utils/cacheUtils';

const VideoCard = ({ 
  content, 
  featured = false, 
  rank = null,
  showRankBadge = false,
  onContentClick,
  onRatingChange,
  onWhatsAppRequest,
  variant = 'default' // 'default', 'hall-of-fame', 'category'
}) => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showFullView, setShowFullView] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentRating, setCurrentRating] = useState(content?.rating);
  const { addItem } = useCartStore();

  const isTopThree = rank <= 3;
  const tierClass = variant === 'hall-of-fame' && isTopThree ? 'lg:col-span-2' : '';

  const getRankBadgeColor = () => {
    if (rank === 1) return 'bg-primary text-background';
    if (rank === 2) return 'bg-secondary text-background';
    if (rank === 3) return 'bg-accent text-white';
    return 'bg-muted text-foreground';
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000)?.toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000)?.toFixed(1)}K`;
    return views?.toString();
  };

  const generateFakeViews = () => {
    return Math.floor(Math.random() * 4900) + 100;
  };

  const handleWhatsAppRequest = async (e) => {
    e?.preventDefault();
    
    if (!userName || !userPhone) {
      setError('Por favor, preencha nome e telefone');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await whatsappService?.createRequest(
        content?.id,
        userName,
        userPhone,
        message
      );
      
      setSuccess(true);
      
      setTimeout(() => {
        whatsappService?.openWhatsApp(content, userName);
        setShowRequestModal(false);
        setSuccess(false);
        setUserName('');
        setUserPhone('');
        setMessage('');
      }, 1000);
    } catch (err) {
      setError(err?.message || 'Erro ao enviar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickWhatsApp = () => {
    if (onWhatsAppRequest) {
      onWhatsAppRequest(content);
    } else {
      const message = encodeURIComponent(
        `Olá UP MIDIA! Gostaria de solicitar: ${content?.title}\n\nTipo: ${content?.type === 'art' ? 'Arte' : 'Vídeo'}\nCategoria: ${content?.category}`
      );
      window.open(`https://wa.me/5511999999999?text=${message}`, '_blank');
    }
  };

  const handleAddToCart = () => {
    addItem({
      id: content?.id,
      type: content?.type || 'content',
      contentType: content?.type,
      title: content?.title,
      name: content?.title,
      thumbnail: content?.thumbnail,
      coverImage: content?.thumbnail,
      thumbnailAlt: content?.thumbnailAlt,
      description: content?.description,
      category: content?.category,
      price: content?.price || 0
    });
  };

  const handleViewContent = () => {
    if (onContentClick) {
      onContentClick(content?.id);
    }
    setShowFullView(true);
  };

  const handleRatingClick = (rating) => {
    if (onRatingChange) {
      setCurrentRating(rating);
      onRatingChange(content?.id, rating);
    }
  };

  // Unified thumbnail logic: thumbnailUrl (custom/auto-generated) > thumbnail > placeholder
  const getPreviewImage = () => {
    if (content?.type === 'video') {
      // Single source of truth: thumbnailUrl is the canonical field
      const url = content?.thumbnailUrl || content?.thumbnail || '/assets/images/no_image.png';
      // Adicionar cache busting para URLs do Hostinger
      return url.includes('/uploads/') ? cacheUtils.addCacheBustToUrl(url) : url;
    }
    return content?.thumbnail || '/assets/images/no_image.png';
  };

  // Render video or image based on content type
  const renderMedia = () => {
    if (content?.type === 'video') {
      const previewImage = getPreviewImage();
      
      return (
        <div className="relative w-full h-full group">
          <Image
            src={previewImage}
            alt={content?.thumbnailAlt || `${content?.title} preview`}
            className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-all duration-300" />
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={handleViewContent}
              className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-primary shadow-lg"
              aria-label="Reproduzir vídeo"
            >
              <Icon name="Play" size={28} color="white" />
            </button>
          </div>
          {/* Cart Button - Always visible for all variants */}
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleAddToCart();
            }}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-accent/90 hover:bg-accent flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg z-10"
            aria-label="Adicionar ao carrinho"
          >
            <Icon name="ShoppingCart" size={18} color="white" />
          </button>
        </div>
      );
    }
    
    return (
      <Image
        src={content?.thumbnail}
        alt={content?.thumbnailAlt}
        className="w-full h-full object-cover transition-smooth hover:scale-105"
      />
    );
  };

  const cardHeight = variant === 'hall-of-fame' && isTopThree ?'h-64 md:h-80 lg:h-96' :'h-48 md:h-56 lg:h-64';

  return (
    <>
      <div
        className={`${tierClass} bg-card rounded-xl overflow-hidden transition-all duration-500 hover:shadow-glow-lg hover:-translate-y-2 hover:scale-[1.02] border border-border/50 hover:border-primary/50`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {showRankBadge && rank && (
          <div className="absolute top-4 left-4 z-10">
            <div className={`${getRankBadgeColor()} px-3 py-1 md:px-4 md:py-2 rounded-lg font-heading font-bold text-sm md:text-base lg:text-lg flex items-center gap-2 shadow-glow-md transition-all duration-300 hover:scale-110`}>
              <Icon name="Award" size={isTopThree ? 20 : 16} className="animate-pulse-neon" />
              <span>#{rank}</span>
            </div>
          </div>
        )}

        <div 
          className={`relative ${variant === 'hall-of-fame' ? cardHeight : 'aspect-video'} overflow-hidden cursor-pointer group`}
          onClick={handleViewContent}
        >
          {renderMedia()}
          
          {variant === 'category' && (
            <div className="absolute top-2 right-2 md:top-3 md:right-3 lg:top-4 lg:right-4">
              <div className="bg-background/90 backdrop-blur-sm rounded-lg px-2 py-1 md:px-3 md:py-1.5 flex items-center gap-1.5 shadow-glow-sm transition-all duration-300 hover:shadow-glow-md hover:scale-105">
                <Icon 
                  name={content?.type === 'art' ? 'Image' : 'Video'} 
                  size={14} 
                  color="var(--color-primary)"
                  className="md:w-4 md:h-4"
                />
                <span className="text-xs md:text-sm font-medium text-primary">
                  {content?.type === 'art' ? 'Arte' : 'Vídeo'}
                </span>
              </div>
            </div>
          )}

          {variant === 'category' && isHovered && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center transition-all duration-500 animate-in fade-in">
              <div className="text-center px-4">
                <Icon name="Maximize2" size={48} color="var(--color-primary)" className="mx-auto mb-2 animate-pulse-neon" />
                <p className="text-xs md:text-sm text-foreground font-medium">
                  Clique para visualizar
                </p>
              </div>
            </div>
          )}

          {content?.watermark && (
            <div className="absolute bottom-3 right-3 opacity-80">
              <img 
                src="/assets/images/up_menor-1769135382338.png" 
                alt="UP MIDIA watermark logo"
                className="h-8 w-auto"
              />
            </div>
          )}
        </div>

        <div className={`p-${variant === 'hall-of-fame' ? '5 md:p-6 lg:p-7' : '5 md:p-6'}`}>
          {variant !== 'hall-of-fame' && (
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1.5 bg-primary/20 text-primary text-xs font-medium rounded-lg shadow-glow-sm transition-all duration-300 hover:shadow-glow-md hover:scale-105">
                {content?.category}
              </span>
              {content?.featured && (
                <Icon name="Award" size={16} color="var(--color-accent)" className="animate-pulse-neon" />
              )}
            </div>
          )}

          {variant === 'hall-of-fame' && (
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className={`font-heading font-bold text-foreground line-clamp-2 ${
                isTopThree ? 'text-lg md:text-xl lg:text-2xl' : 'text-base md:text-lg'
              }`}>
                {content?.title}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Icon name="Star" size={isTopThree ? 20 : 16} color="var(--color-primary)" fill="var(--color-primary)" />
                <span className={`font-bold text-primary ${isTopThree ? 'text-lg md:text-xl' : 'text-base'}`}>
                  {content?.rating?.toFixed(1)}
                </span>
              </div>
            </div>
          )}

          {variant !== 'hall-of-fame' && (
            <h3 className="text-base md:text-lg font-heading font-bold text-foreground mb-2 line-clamp-2">
              {content?.title}
            </h3>
          )}

          {content?.price > 0 && variant !== 'hall-of-fame' && (
            <div className="mb-3">
              <span className="text-lg font-bold text-accent">
                R$ {content?.price?.toFixed(2)}
              </span>
            </div>
          )}

          {variant === 'hall-of-fame' && (
            <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mb-3">
              <Icon name="FolderOpen" size={14} />
              <span className="line-clamp-1">{content?.category}</span>
            </div>
          )}

          {variant === 'category' ? (
            <div className="flex items-center gap-1 mb-3 md:mb-4">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  onClick={() => handleRatingClick(star)}
                  className="transition-smooth hover:scale-110 focus-ring rounded"
                  aria-label={`Avaliar com ${star} estrela${star > 1 ? 's' : ''}`}
                >
                  <Icon
                    name="Star"
                    size={16}
                    color={star <= currentRating ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    className={`md:w-5 md:h-5 ${star <= currentRating ? 'fill-primary' : ''}`}
                  />
                </button>
              ))}
              <span className="ml-2 text-xs md:text-sm text-muted-foreground font-medium">
                {currentRating?.toFixed(1)}
              </span>
            </div>
          ) : variant !== 'hall-of-fame' && (
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)]?.map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={16}
                  color={i < content?.rating ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                  className={i < content?.rating ? 'fill-primary' : ''}
                />
              ))}
              <span className="text-xs md:text-sm text-muted-foreground ml-1">
                {content?.rating?.toFixed(1)}
              </span>
            </div>
          )}

          {variant === 'hall-of-fame' && (
            <div className="flex items-center justify-between gap-4 pt-2 border-t border-border mb-3">
              <div className="flex items-center gap-4 md:gap-6">
                <div className="flex items-center gap-1.5">
                  <Icon name="Eye" size={16} color="var(--color-secondary)" />
                  <span className="text-xs md:text-sm font-medium text-foreground">
                    {formatViews(generateFakeViews())}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="default"
              iconName="ShoppingCart"
              iconPosition="left"
              onClick={handleAddToCart}
              className="flex-1"
            >
              {variant === 'hall-of-fame' ? <span className="hidden sm:inline">Adicionar</span> : 'Adicionar'}
            </Button>
            <Button
              variant="outline"
              size="default"
              iconName="MessageCircle"
              iconPosition={variant === 'hall-of-fame' ? undefined : 'left'}
              onClick={() => variant === 'default' ? setShowRequestModal(true) : handleQuickWhatsApp()}
            >
              {variant === 'hall-of-fame' ? <span className="hidden sm:inline">Solicitar</span> : 'WhatsApp'}
            </Button>
          </div>

          {content?.badge && variant === 'hall-of-fame' && (
            <div className="flex items-center gap-2 pt-2">
              <div className="px-3 py-1 bg-accent/20 text-accent rounded-md text-xs font-medium flex items-center gap-1.5">
                <Icon name="TrendingUp" size={14} />
                <span>{content?.badge}</span>
              </div>
            </div>
          )}
        </div>

        {showRequestModal && variant === 'default' && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg p-6 max-w-md w-full glow-primary">
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                Solicitar via WhatsApp
              </h3>
              
              {success ? (
                <div className="text-center py-4">
                  <div className="text-green-500 text-5xl mb-4">✓</div>
                  <p className="text-foreground">Solicitação enviada com sucesso!</p>
                  <p className="text-sm text-muted-foreground mt-2">Abrindo WhatsApp...</p>
                </div>
              ) : (
                <form onSubmit={handleWhatsAppRequest} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500 rounded text-sm text-red-500">
                      {error}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Seu Nome *
                    </label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e?.target?.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                      placeholder="Digite seu nome"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      value={userPhone}
                      onChange={(e) => setUserPhone(e?.target?.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                      placeholder="(11) 99999-9999"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Mensagem (opcional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e?.target?.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                      rows="3"
                      placeholder="Informações adicionais..."
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowRequestModal(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>

      {showFullView && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowFullView(false)}
        >
          <button
            onClick={() => setShowFullView(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-card/80 rounded-full flex items-center justify-center glow-primary transition-smooth hover:glow-primary-hover z-10"
            aria-label="Fechar"
          >
            <Icon name="X" size={24} color="var(--color-primary)" />
          </button>
          
          <div className="max-w-7xl max-h-[90vh] w-full" onClick={(e) => e?.stopPropagation()}>
            {content?.type === 'video' && content?.videoUrl ? (
              <video 
                className="w-full h-full max-h-[90vh] object-contain"
                controls
                autoPlay
                preload="metadata"
              >
                <source src={content?.videoUrl} type="video/mp4" />
                Seu navegador não suporta vídeos.
              </video>
            ) : (
              <img
                src={content?.thumbnail}
                alt={content?.thumbnailAlt}
                className="w-full h-full max-h-[90vh] object-contain"
              />
            )}
            {content?.watermark && (
              <div className="absolute bottom-8 right-8 opacity-80">
                <img 
                  src="/assets/images/up_menor-1769135382338.png" 
                  alt="UP MIDIA watermark logo"
                  className="h-12 w-auto"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoCard;
